import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Construction } from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import axios from 'axios';
import PageSEO from '../components/PageSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const statusLabels = {
  planifie: { label: 'Planifié', color: 'bg-blue-100 text-blue-700' },
  en_cours: { label: 'En cours', color: 'bg-gold-400/20 text-gold-700' },
  termine: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700' },
};

const getImgUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/uploads/') || url.startsWith('/api/files/')) return `${BASE_URL}${url}`;
  return url;
};

const ProjectDetailPage = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/projects/${id}`)
      .then(res => setProject(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <main className="pt-14 md:pt-16">
        <div className="container-main py-20 flex justify-center"><div className="loader"></div></div>
      </main>
    );
  }

  if (!project) {
    return (
      <main className="pt-14 md:pt-16" data-testid="project-not-found">
        <div className="container-main py-20 text-center">
          <Construction className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-bold text-neutral-800 mb-2">Projet non trouvé</h2>
          <p className="text-neutral-500 mb-6">Ce projet n'existe pas ou n'est plus disponible.</p>
          <Link to="/projets"><Button>Retour aux projets</Button></Link>
        </div>
      </main>
    );
  }

  const status = statusLabels[project.status] || { label: project.status, color: 'bg-neutral-100' };

  return (
    <main className="pt-14 md:pt-16" data-testid="project-detail-page">
      <PageSEO title={project.title} description={project.description} path={`/projets/${id}`} />

      <section className="bg-emerald-700 py-12 md:py-16">
        <div className="container-main">
          <Link to="/projets" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors">
            <ArrowLeft size={18} /> Retour aux projets
          </Link>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-white">{project.title}</h1>
          <div className="flex items-center gap-4 mt-4">
            <span className={`text-sm font-semibold px-4 py-1.5 rounded-full ${status.color}`}>{status.label}</span>
            {project.start_date && (
              <span className="flex items-center gap-2 text-white/70 text-sm">
                <Calendar size={14} /> {project.start_date}
              </span>
            )}
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="container-main max-w-4xl">
          {project.image_url && (
            <img src={getImgUrl(project.image_url)} alt={project.title} className="w-full rounded-xl mb-8" style={{ maxHeight: '400px', objectFit: 'contain', background: '#f5f7fa' }} />
          )}

          {project.progress > 0 && (
            <div className="mb-8 p-6 bg-neutral-50 rounded-xl">
              <div className="flex justify-between text-sm mb-2">
                <span className="font-medium text-neutral-700">Avancement</span>
                <span className="font-bold text-emerald-600">{project.progress}%</span>
              </div>
              <Progress value={project.progress} className="h-3" />
            </div>
          )}

          <div className="prose prose-lg max-w-none text-neutral-600">
            {project.description.split('\n').map((p, i) => <p key={i}>{p}</p>)}
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {project.budget && (
              <div className="p-4 bg-emerald-50 rounded-xl">
                <span className="text-sm text-neutral-500">Budget</span>
                <p className="font-semibold text-emerald-700 text-lg">{project.budget}</p>
              </div>
            )}
            {project.end_date && (
              <div className="p-4 bg-amber-50 rounded-xl">
                <span className="text-sm text-neutral-500">Date de fin prévue</span>
                <p className="font-semibold text-amber-700 text-lg">{project.end_date}</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProjectDetailPage;
